import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const storeTokens = (accessToken, refreshToken, userId) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('userId', userId);
  };

  const clearTokens = () => localStorage.clear();

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authAPI.me();
      setUser(data.data.user);
      connectSocket(data.data.user._id);
    } catch {
      clearTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    const { user: u, accessToken, refreshToken } = data.data;
    storeTokens(accessToken, refreshToken, u._id);
    setUser(u);
    connectSocket(u._id);
    return u;
  };

  const register = async (name, email, password) => {
    const { data } = await authAPI.register({ name, email, password });
    const { user: u, accessToken, refreshToken } = data.data;
    storeTokens(accessToken, refreshToken, u._id);
    setUser(u);
    connectSocket(u._id);
    return u;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch { /* best effort */ }
    disconnectSocket();
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
